import React, { useState } from 'react';
import { CheckCircle2, XCircle, Play, Loader2, Download } from 'lucide-react';
import { runBrowseRegression, RegressionCase } from '@/lib/browseRegression';

/**
 * Admin -> Security panel.
 *
 * 1. Runs the permanent mobile browse regression suite (the "51 listings in the
 *    database, Zero listings on mobile" guard).
 * 2. Exports the canonical URL inventory CSV straight from the live sitemap, so
 *    the SEO audit spreadsheet can never drift from the routes actually served.
 */
const BrowseRegressionPanel: React.FC = () => {
  const [cases, setCases] = useState<RegressionCase[] | null>(null);
  const [running, setRunning] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvNote, setCsvNote] = useState('');

  const run = async () => {
    setRunning(true);
    setCases(null);
    try {
      setCases(await runBrowseRegression());
    } catch (e) {
      setCases([{ name: 'Suite crashed', passed: false, detail: String(e) }]);
    }
    setRunning(false);
  };

  /** Fetch the live sitemap and turn it into the audit inventory CSV. */
  const exportInventory = async () => {
    setCsvBusy(true);
    setCsvNote('');
    try {
      const res = await fetch(`${window.location.origin}/sitemap.xml`);
      const xml = await res.text();
      const locs = [...xml.matchAll(/<loc>([^<]+)<\/loc>/g)].map((m) => m[1]);
      const lastmods = [...xml.matchAll(/<lastmod>([^<]+)<\/lastmod>/g)].map((m) => m[1]);

      if (locs.length === 0) {
        setCsvNote('No URLs found — is /sitemap.xml routed to the sitemap function?');
        setCsvBusy(false);
        return;
      }

      const classify = (u: string) => {
        const p = u.replace(/^https?:\/\/[^/]+/, '').replace(/^\/(en|es)/, '');
        if (p === '' || p === '/') return 'core:home';
        if (p.startsWith('/trades/') || p.startsWith('/oficios/')) return 'trade';
        if (p.includes('/counties/') || p.includes('/condados/')) return 'county';
        if (p.startsWith('/california/')) return 'city';
        if (p.startsWith('/leads/')) return 'money';
        if (p.startsWith('/resources/') || p.startsWith('/recursos/')) return 'guide';
        if (p.startsWith('/jobs/') || p.startsWith('/trabajos/')) return 'listing';
        return 'core';
      };

      const rows = [
        [
          'url', 'language', 'page_type', 'entity_slug', 'canonical_url', 'indexable',
          'in_sitemap', 'lastmod',
        ].join(','),
        ...locs.map((u, i) => {
          const lang = /\/es(\/|$)/.test(u) ? 'es' : 'en';
          const type = classify(u);
          const slug = u.split('/').filter(Boolean).pop() || '';
          return [u, lang, type, slug, u, 'index,follow', 'yes', lastmods[i] || ''].join(',');
        }),
      ].join('\n');

      const counts = locs.reduce<Record<string, number>>((acc, u) => {
        const k = classify(u);
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {});
      const en = locs.filter((u) => !/\/es(\/|$)/.test(u)).length;

      const blob = new Blob([rows], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `giggone-url-inventory-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);

      setCsvNote(
        `${locs.length} canonical URLs (${en} EN / ${locs.length - en} ES). ` +
          Object.entries(counts)
            .map(([k, v]) => `${k}: ${v}`)
            .join(' · '),
      );
    } catch (e) {
      setCsvNote(`Could not read the sitemap: ${String(e)}`);
    }
    setCsvBusy(false);
  };

  const failed = cases?.filter((c) => !c.passed).length ?? 0;

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      <h2 className="text-base font-extrabold text-slate-900">Mobile browse regression &amp; URL inventory</h2>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">
        Permanent guard for the “listings in the database but Zero listings on mobile” failure. It compares what the
        marketplace would render against what the browse endpoint actually returned, verifies the 60-row cap, confirms no
        protected city, ZIP, contact, owner id or document path is present, and confirms a normal session is not rate
        limited.
      </p>

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          onClick={run}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {running ? 'Running...' : 'Run regression suite'}
        </button>
        <button
          onClick={exportInventory}
          disabled={csvBusy}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
        >
          {csvBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export URL inventory CSV
        </button>
      </div>

      {csvNote && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-xs font-medium text-slate-600">{csvNote}</p>}

      {cases && (
        <div className="mt-5">
          <p className={`text-sm font-bold ${failed === 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {failed === 0 ? `PASS — ${cases.length}/${cases.length} checks` : `FAIL — ${failed} of ${cases.length} checks failed`}
          </p>
          <ul className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200">
            {cases.map((c) => (
              <li key={c.name} className="flex items-start gap-3 p-3">
                {c.passed ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                )}
                <div>
                  <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default BrowseRegressionPanel;
